<template>
  <div id="knowledge-base">
    <v-row>
      <v-col cols="12" md="3" lg="3" order="1" order-md="2" class="knowledge-base-summary-overlay">
        <v-card class="knowledge-base-summary" style="overflow-y: hidden">
          <div class="kb-questions py-1">
            <div
              v-for="subTitle in summary"
              :key="subTitle.title"
              class="kb-question d-flex align-start text-sm px-5 my-4 cursor-pointer"
            >
              <v-icon size="14" class="me-2 mt-1" color="secondary">
                {{ icons.mdiCheckboxBlankCircleOutline }}
              </v-icon>
              <span class="text--secondary"
                    @click="scrollToElement(subTitle.link)">{{ subTitle.title }}</span>
            </div>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" md="9" lg="9" order="1" order-md="2">
        <v-card>
          <v-card-text>
            <h2>Notifications</h2>
          </v-card-text>
          <v-card-title id="what-notifications">1. What Are Notifications?</v-card-title>
          <v-card-text><b>Notifications</b> are system events generated by the Merchanto Prevent platform whenever the status of a dispute case changes.</v-card-text>
          <v-card-text>
            <ul>
              <li>They allow merchants to keep their systems in sync with the dispute lifecycle.</li>
              <li>Notifications are delivered via <b>HTTP POST webhooks</b> to merchant-provided endpoints.</li>
              <li>If a webhook is not implemented, notifications will still be visible in the <b>Merchanto platform</b>, but will <b>not</b> be sent in real time.</li>
            </ul>
          </v-card-text>
          <v-card-text>Implementing notifications is <b>optional</b>, but strongly recommended if you want real-time updates instead of relying only on the platform dashboard.</v-card-text>
          <v-divider></v-divider>

          <v-card-title id="when-notifications-generated">2. When Are Notifications Generated?</v-card-title>
          <v-card-text>A notification is created when the status of a transaction case changes, for example:</v-card-text>
          <v-card-text>
            <ul>
              <li>A dispute was <b>successfully deflected</b> <code>(caseStatus = new)</code>.</li>
              <li>A dispute was <b>not deflected and escalated</b> <code>(caseStatus = failed)</code>.</li>
              <li>A previously deflected case was reopened <code>(caseStatus = delete)</code>.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="endpoint-setup">3. Endpoint Setup</v-card-title>
          <v-card-text>
            <ul>
              <li>You may provide <b>one webhook per integration</b> (Visa, Mastercard, or both).</li>
              <li>If you integrate with only Visa → provide a Visa notification webhook.</li>
              <li>If you integrate with both Visa and Mastercard → provide one webhook per scheme.</li>
            </ul>
          </v-card-text>
          <v-card-text>
            <p><b>Example endpoints:</b></p>
            <ul>
              <li>Visa Notifications: <code>POST /api/prevent/visa/notifications</code></li>
              <li>Mastercard Notifications: <code>POST /api/prevent/mastercard/notifications</code></li>
            </ul>
          </v-card-text>
          <v-card-text>
            These paths are examples only. You can use your own (e.g., <code>/callbacks/prevent/visa</code>) provided that:
          </v-card-text>
          <v-card-text>
            <ul>
              <li>They accept <b>POST</b> requests.</li>
              <li>They are secured with <b>HTTPS</b>.</li>
              <li>They do not contain query parameters.</li>
              <li>They require <b>Basic Authentication</b>.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="security-authentication">4. Security & Authentication</v-card-title>
          <v-card-text>
            <ul>
              <li>Merchanto sends all webhook requests using <b>Basic Auth</b>.</li>
              <li>
                Header format:
                <br/>
                <code>Authorization: Basic &lt;Base64Encoded(username:password)></code>
              </li>
              <li>Your system must validate the credentials before processing the payload.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="request-payload">5. Notification Request Payload</v-card-title>
          <v-card-text>A typical notification request looks like this:</v-card-text>
          <v-card-text>
            <pre class="output"><code>
            curl -X POST "https://your-domain.com/api/prevent/{visa|mastercard}/notifications" \
              --user username:password \
              -H "Content-Type: application/json" \
              -d '{
                "caseId": "9b1b1baf-358c-3365-8f1f-d1c18affb918”,
                "caseStatus": "new",
                "caseDate": "2022-10-25T09:17:28Z",
                "cardBin": "411111",
                "cardLast4": "1111",
                "paymentType": "Visa",
                "transactionDate": "2022-10-25T09:17:28Z",
                "transactionAmount": {
                  "amount": 100.25,
                  "currency": "USD"
                },
                "arn": "24692162324102146309799",
                "authCode": "368525",
                "acquirerBin": "469216",
                "cardAcceptorId": "DP210000856448",
                "paymentDescriptor": "ONLINE*MERCHANT",
                "mcsn": "01",
                "purchaseIdentifier": "WEB05549686048",
                "transactionId": "2837347837383",
                "linkedTransactionId": "0eaf65c9-ce07-4da2-aece-9ca5cf4f8651",
                "transactionType": "SALE",
                "mcc": "4899",
                "posEntryModeCode": "06"
              }'
            </code></pre>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="payload-structure">6. Payload structure</v-card-title>
          <v-card-text>The payload is structured as key-value pairs. Each field in the payload structure is described using the following columns:</v-card-text>
          <v-card-text>
            <ul>
              <li><b>Field</b> &mdash; JSON property name used in the notification payload.</li>
              <li><b>Type</b> &mdash; Data type of the field (string, number, object, etc.).</li>
              <li><b>Availability</b> &mdash; Indicates whether the field is always present, conditional, or optional.</li>
              <li><b>Description</b> &mdash; Explanation of the field, including usage notes and scheme-specific details (e.g., Visa only).</li>
            </ul>
          </v-card-text>
          <v-card-text>Merchants should implement parsing logic that accounts for <b>Conditional</b> and <b>Optional</b> fields possibly being absent.</v-card-text>
          <v-card-text>
            <h3>Notification Object</h3>
            <v-simple-table class="payload-example-table">
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Field</th>
                  <th class="text-left">Type</th>
                  <th class="text-left">Availability</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>caseId</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>36</i></p>
                  </td>
                  <td>always</td>
                  <td>Case ID generated by Merchanto (UUID v4).</td>
                </tr>
                <tr>
                  <td><b>caseStatus</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>10</i></p>
                    <p><b>enum:</b>&nbsp;<i>[new, delete, failed, timeout]</i></p>
                  </td>
                  <td>always</td>
                  <td>Lifecycle status of the case. Enum: <code>new</code>, <code>delete</code>, <code>failed</code>, <code>timeout</code>.</td>
                </tr>
                <tr>
                  <td><b>caseDate</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>format:</b>&nbsp;<i>date-time</i></p>
                  </td>
                  <td>always</td>
                  <td>Date the notification was generated (UTC, ISO 8601).</td>
                </tr>
                <tr>
                  <td><b>cardBin</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>8</i></p>
                  </td>
                  <td>conditional</td>
                  <td>Issuer card BIN (6 digits).</td>
                </tr>
                <tr>
                  <td><b>cardLast4</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>4</i></p>
                  </td>
                  <td>conditional</td>
                  <td>Last four digits of the card number associated with the transaction.</td>
                </tr>
                <tr>
                  <td><b>paymentType</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>20</i></p>
                    <p><b>enum:</b>&nbsp;<i>[visa, masterCard, americanExpress, discover, other]</i></p>
                  </td>
                  <td>always</td>
                  <td>Payment type: <code>visa</code>, <code>masterCard</code>, <code>americanExpress</code>, <code>discover</code>, <code>other</code>. Indicates the payment type of the transaction.</td>
                </tr>
                <tr>
                  <td><b>transactionDate</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>required:</b>&nbsp;<i>true</i></p>
                    <p><b>format:</b>&nbsp;<i>date-time</i></p>
                  </td>
                  <td>always</td>
                  <td>Date/time of the original transaction (UTC).</td>
                </tr>
                <tr>
                  <td><b>transactionAmount</b></td>
                  <td>
                    <p><i>object</i></p>
                  </td>
                  <td>always</td>
                  <td>TransactionAmountObject.</td>
                </tr>
                <tr>
                  <td><b>arn</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>24</i></p>
                  </td>
                  <td>conditional</td>
                  <td>Acquirer Reference Number (ARN).</td>
                </tr>
                <tr>
                  <td><b>authCode</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>6</i></p>
                  </td>
                  <td>conditional</td>
                  <td>Issuer authorization code.</td>
                </tr>
                <tr>
                  <td><b>acquirerBin</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>6</i></p>
                  </td>
                  <td>optional</td>
                  <td>Acquiring Bank identifier (Visa only).</td>
                </tr>
                <tr>
                  <td><b>cardAcceptorId</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>15</i></p>
                  </td>
                  <td>optional</td>
                  <td>Merchant account identifier (Visa only).</td>
                </tr>
                <tr>
                  <td><b>paymentDescriptor</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>25</i></p>
                  </td>
                  <td>always</td>
                  <td>Merchant billing descriptor (statement text).</td>
                </tr>
                <tr>
                  <td><b>mcsn</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>2</i></p>
                  </td>
                  <td>optional</td>
                  <td>Multiple Clearing Sequence Number (Visa only).</td>
                </tr>
                <tr>
                  <td><b>purchaseIdentifier</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>25</i></p>
                  </td>
                  <td>optional</td>
                  <td>Merchant-defined order/invoice reference.</td>
                </tr>
                <tr>
                  <td><b>transactionId</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>50</i></p>
                  </td>
                  <td>always</td>
                  <td>Unique transaction identifier from the card network.</td>
                </tr>
                <tr>
                  <td><b>linkedTransactionId</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>36</i></p>
                  </td>
                  <td>always</td>
                  <td>Linked transaction ID (UUID v4) from Merchanto.</td>
                </tr>
                <tr>
                  <td><b>transactionType</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>20</i></p>
                  </td>
                  <td>optional</td>
                  <td>Transaction type (e.g., <code>SALE</code>).</td>
                </tr>
                <tr>
                  <td><b>mcc</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>4</i></p>
                  </td>
                  <td>optional</td>
                  <td>Merchant Category Code (Visa only).</td>
                </tr>
                <tr>
                  <td><b>posEntryModeCode</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>2</i></p>
                  </td>
                  <td>optional</td>
                  <td>POS entry mode code (01 = manual, 06 = key entered, 07 = contactless chip, 10 = credentials on file, etc.).</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>

          <v-card-text>
            <h3>TransactionAmount Object</h3>
            <v-simple-table class="payload-example-table">
              <template #default>
                <thead>
                <tr>
                  <th class="text-left">Field</th>
                  <th class="text-left">Type</th>
                  <th class="text-left">Availability</th>
                  <th class="text-left">Description</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td><b>amount</b></td>
                  <td>
                    <p><i>number</i></p>
                    <p><b>maximum:</b>&nbsp;<i>10</i></p>
                  </td>
                  <td>always</td>
                  <td>Amount value in multiples of 0.01 (e.g., 100.25).</td>
                </tr>
                <tr>
                  <td><b>currency</b></td>
                  <td>
                    <p><i>string</i></p>
                    <p><b>maxLength:</b>&nbsp;<i>3</i></p>
                  </td>
                  <td>always</td>
                  <td>ISO 4217 3-letter currency code.</td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="notification-lifecycle">7. Notification Lifecycle</v-card-title>
          <v-card-text>
            <ul>
              <li><b>new</b> → Case successfully deflected.</li>
              <li><b>failed</b> → Case escalated to dispute.</li>
              <li><b>delete</b> → Previously deflected case was reopened or disputed again.</li>
            </ul>
          </v-card-text>
          <v-divider></v-divider>

          <v-card-title id="merchant-responsibilities">8. Merchant Responsibilities</v-card-title>
          <v-card-text>
            <ul>
              <li><b>Optional integration:</b> Notifications can be skipped, but then you must monitor cases in the Merchanto platform manually.</li>
              <li>
                <b>If implemented:</b>
                <ul>
                  <li>
                    Acknowledge each notification with <code>HTTP 200 OK</code>.
                  </li>
                  <li>
                    Validate Basic Auth credentials.
                  </li>
                  <li>
                    Store and process notifications to update your internal dispute-handling workflows in real time.
                  </li>
                </ul>
              </li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import {
  mdiCogOutline,
  mdiCheckboxBlankCircleOutline,
  mdiCellphoneCog,
  mdiChevronLeft,
} from '@mdi/js';
import useDocs from '@/views/docs/useDocs';

export default {
  setup() {
    const { scrollToElement } = useDocs();

    const summary = [
      { title: '1. What Are Notifications?', link: 'what-notifications' },
      { title: '2. When Are Notifications Generated?', link: 'when-notifications-generated' },
      { title: '3. Endpoint Setup', link: 'endpoint-setup' },
      { title: '4. Security & Authentication', link: 'security-authentication' },
      { title: '5. Notification Request Payload', link: 'request-payload' },
      { title: '6. Payload structure', link: 'payload-structure' },
      { title: '7. Notification Lifecycle', link: 'notification-lifecycle' },
      { title: '8. Merchant Responsibilities', link: 'merchant-responsibilities' },
    ];

    return {
      summary,
      icons: {
        mdiCogOutline,
        mdiCheckboxBlankCircleOutline,
        mdiCellphoneCog,
        mdiChevronLeft,
      },
      scrollToElement,
    };
  },
};
</script>

<style lang="scss">
@import '@core/preset/preset/pages/knowledge-base';
</style>
